import {
  Addendum,
  AddendumSession,
  FctReport,
  KeyImage,
  QualityControlCategoryItemWithUuid,
  ReportOrgan,
} from '@/api';
import { FindingsByOrgan } from '@/shared/model';

interface ResolveAddendumSessionArgs {
  activeSession: AddendumSession | null;
  addenda: Addendum[];
}

interface ResolvedAddendum {
  addendumText: string;
  addendumUuid: null | string;
}

export function resolveAddendumSession({
  activeSession,
  addenda,
}: ResolveAddendumSessionArgs): ResolvedAddendum {
  const addendumUuid = activeSession?.status === 'active' ? activeSession.addendumUuid : null;

  if (!addendumUuid) {
    return {
      addendumText: '',
      addendumUuid: null,
    };
  }

  const activeAddendum = addenda.find((addendum) => addendum.uuid === addendumUuid);

  return {
    addendumText: activeAddendum?.summary ?? '',
    addendumUuid,
  };
}

export function hasReportLevelAddendumChanges(report: FctReport): boolean {
  return Boolean(report.comparisonAddendumStatus || report.techniqueAddendumStatus);
}

export function hasKeyImagesAddendumChanges(
  keyImages: Array<Pick<KeyImage, 'addendumStatus'>>,
): boolean {
  return keyImages.some((keyImage) => keyImage.addendumStatus);
}

export function hasFindingAddendumChanges(findings: FindingsByOrgan): boolean {
  return Object.values(findings).some((organFindings) =>
    organFindings?.some((finding) => finding.renderedSummary?.addendumStatus),
  );
}

export function hasOrganNormalsAddendumChanges(reportOrgans: ReportOrgan[]): boolean {
  return reportOrgans.some((reportOrgan) => reportOrgan.addendumStatus);
}

export function hasQualityControlAddendumChanges(
  qualityControlItems: QualityControlCategoryItemWithUuid[],
): boolean {
  return qualityControlItems.some((item) => item.addendumStatus);
}

interface HasAddendumChangesArgs {
  findings: FindingsByOrgan;
  keyImages: Array<Pick<KeyImage, 'addendumStatus'>>;
  qualityControlItems?: QualityControlCategoryItemWithUuid[];
  report: FctReport;
  reportOrgans: ReportOrgan[];
}

export function hasAddendumChanges({
  findings,
  keyImages,
  qualityControlItems = [],
  report,
  reportOrgans,
}: HasAddendumChangesArgs): boolean {
  return (
    hasReportLevelAddendumChanges(report) ||
    hasKeyImagesAddendumChanges(keyImages) ||
    hasFindingAddendumChanges(findings) ||
    hasOrganNormalsAddendumChanges(reportOrgans) ||
    hasQualityControlAddendumChanges(qualityControlItems)
  );
}
